/**
 * Team Review Service
 * Handles all API calls related to team reviews
 */

import { apiService } from './api';

// Feedback interfaces
export interface TeamReviewFeedback {
  employeeFeedbackRating: string;
  appreciativeFeedback: string[];
  constructiveFeedback: string[];
}

export interface TeamReviewFeedbackResponse {
  status: boolean;
  data: TeamReviewFeedback | null;
  message?: string;
  error?: string;
}

// OKRs interfaces
export interface TeamReviewOKR {
  sno: number;
  objective: string;
  target: string;
  endYearResults: string;
  rating: number;
  manager_rating?: number; // Optional manager rating field
}

export interface TeamReviewOKRsData {
  okrs: TeamReviewOKR[];
  manager_rating?: number; // Include manager rating in the response data
  manager_comments?: string; // Superior comments submitted with the OKR rating
}

export interface TeamReviewOKRsResponse {
  status: boolean;
  data: TeamReviewOKRsData | null;
  message?: string;
  error?: string;
}

interface TeamReviewData {
  id: string;
  employeeId: string;
  employeeName: string;
  designation: string;
  department: string;
  managerName: string;
  reviewStatus: 'Pending' | 'Submitted';
  employeeEmail: string;
}

interface TeamReviewListResponse {
  status: boolean;
  data: {
    team_review_list: any[];
  };
  message?: string;
  error?: string;
}

class TeamReviewService {
  /**
   * Get team review list for a manager
   */
  async getTeamReviewList(managerEmail: string): Promise<TeamReviewListResponse> {
    try {
      console.log('🔄 TeamReviewService - Fetching team review list for:', managerEmail);
      
      const response = await apiService.post('/team-review/list', {
        manager_email: managerEmail
      });
      
      console.log('📋 TeamReviewService - API Response:', response);
      
      return {
        status: true,
        data: (response.data as any) || { team_review_list: [] },
        message: 'Team reviews fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error fetching team reviews:', error);
      
      return {
        status: false,
        data: { team_review_list: [] },
        error: error.response?.data?.message || error.message || 'Failed to fetch team reviews'
      };
    }
  }

  /**
   * Transform API data to component format
   */
  transformApiData(apiData: any[]): TeamReviewData[] {
    if (!Array.isArray(apiData)) {
      console.warn('⚠️ TeamReviewService - Invalid API data format, expected array');
      return [];
    }

    return apiData.map((item: any) => ({
      id: item.id || item.review_id || String(Math.random()),
      employeeId: item.employee_id || item.employeeId || '',
      employeeName: item.employee_name || item.employeeName || '',
      designation: item.designation || '',
      department: item.department || '',
      managerName: item.manager_name || item.managerName || '',
      reviewStatus: item.review_status || item.reviewStatus || 'Pending',
      employeeEmail: item.employee_email || item.employeeEmail || ''
    }));
  }

  /**
   * Get team review nomination list
   */
  async getTeamNominationReviewList(email: string): Promise<any> {
    try {
      console.log('🔄 TeamReviewService - Fetching team nomination review list for:', email);
      
      const response = await apiService.get(`/api/v1/feedback/team_nomination_review_list/?email=${email}`);
      
      console.log('📋 TeamReviewService - Team nomination review list response:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Team nomination review list fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error fetching team nomination review list:', error);
      
      return {
        status: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch team nomination review list'
      };
    }
  }

  /**
   * Get team review details by ID
   */
  async getTeamReviewDetails(reviewId: string, managerEmail: string): Promise<any> {
    try {
      console.log('🔄 TeamReviewService - Fetching review details for:', reviewId);
      
      const response = await apiService.post('/team-review/details', {
        review_id: reviewId,
        manager_email: managerEmail
      });
      
      console.log('📋 TeamReviewService - Review details response:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Review details fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error fetching review details:', error);
      
      return {
        status: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch review details'
      };
    }
  }
  /**
   * Get team review feedback by employee ID
   */
  async getTeamReviewFeedback(employeeId: string, managerEmail: string): Promise<TeamReviewFeedbackResponse> {
    try {
      console.log('🔄 TeamReviewService - Fetching feedback for employee:', employeeId);
      
      const response = await apiService.post('/team-review/feedback', {
        employee_id: employeeId,
        manager_email: managerEmail
      });
      
      console.log('📋 TeamReviewService - Feedback response:', response);
      
      return {
        status: true,
        data: response.data as unknown as TeamReviewFeedback,
        message: 'Feedback fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error fetching feedback:', error);
      
      // Return dummy data for now
      const dummyData: TeamReviewFeedback = {
        employeeFeedbackRating: "3.4/5.0",
        appreciativeFeedback: [
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
        ],
        constructiveFeedback: [
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.",
          "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat."
        ]
      };
      
      return {
        status: false,
        data: dummyData,
        error: error.response?.data?.message || error.message || 'Failed to fetch feedback, using dummy data'
      };
    }
  }

  /**
   * Get team review OKRs by employee ID (legacy method)
   */
  async getTeamReviewOKRs(employeeId: string, managerEmail: string): Promise<TeamReviewOKRsResponse> {
    try {
      console.log('🔄 TeamReviewService - Fetching OKRs for employee:', employeeId);
      
      const response = await apiService.post('/team-review/okrs', {
        employee_id: employeeId,
        manager_email: managerEmail
      });
      
      console.log('📋 TeamReviewService - OKRs response:', response);
      
      return {
        status: true,
        data: response.data as unknown as TeamReviewOKRsData,
        message: 'OKRs fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error fetching OKRs:', error);
      
      // Return dummy data for now
      const dummyData: TeamReviewOKRsData = {
        okrs: [
          {
            sno: 1,
            objective: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
            target: "Lorem ipsum",
            endYearResults: "Lorem",
            rating: 5
          },
          {
            sno: 2,
            objective: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
            target: "Lorem ipsum",
            endYearResults: "Ipsum",
            rating: 5
          },
          {
            sno: 3,
            objective: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
            target: "Lorem ipsum",
            endYearResults: "Lorsum",
            rating: 5
          },
          {
            sno: 4,
            objective: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
            target: "Lorem ipsum",
            endYearResults: "Lorsum",
            rating: 4
          },
          {
            sno: 5,
            objective: "Lorem ipsum dolor sit amet, consectetur adipiscing elit",
            target: "Lorem ipsum",
            endYearResults: "Lorsum",
            rating: 3
          }
        ]
      };
      
      return {
        status: false,
        data: dummyData,
        error: error.response?.data?.message || error.message || 'Failed to fetch OKRs, using dummy data'
      };
    }
  }

  /**
   * Get self appraisal OKRs by email and employee ID (for admin view)
   */
  async getSelfAppraisalOKRs(email: string, empId: string): Promise<TeamReviewOKRsResponse> {
    try {
      console.log('===== SERVICE: STARTING OKRS API CALL =====');
      console.log('SERVICE Step 1 - Input parameters:');
      console.log('  - Email:', email);
      console.log('  - Employee ID:', empId);
      
      const apiUrl = `/api/v1/feedback/admin/self_appraisal/okr/?email=${encodeURIComponent(email)}&emp_id=${encodeURIComponent(empId)}`;
      console.log('  - Full API URL:', apiUrl);
      
      console.log('SERVICE Step 2 - Making API call...');
      const response = await apiService.get(apiUrl);

   
      
      console.log('SERVICE Step 3 - Got API response:');
      console.log('  - Response exists:', !!response);
      console.log('  - Response.data exists:', !!response.data);
      console.log('  - Response type:', typeof response);
      console.log('  - Response.data type:', typeof response.data);
      console.log('SERVICE - Full response object:', response);
      console.log('SERVICE - Raw response.data:', JSON.stringify(response.data, null, 2));
      
      // Transform the response data to match our interface
      let transformedOKRs: TeamReviewOKR[] = [];
      
      // Check if response has the expected structure
      console.log('🔍 Debug - response.data:', response.data);
      console.log('🔍 Debug - response.data.status:', (response.data as any)?.status);
      console.log('🔍 Debug - self_appraisal_list:', (response.data as any)?.self_appraisal_list);
      console.log('🔍 Debug - self_appraisal_list[0]:', (response.data as any)?.self_appraisal_list?.[0]);
      console.log('🔍 Debug - okrs array:', (response.data as any)?.self_appraisal_list?.[0]?.okrs);
      
      // Extract OKRs directly from the correct API response path
      // API structure: response.data.self_appraisal_list[0].okrs (not nested under .data)
      const apiResponseData = (response.data as any);
      
      // Use the correct extraction path that works in the alert
      const okrs = apiResponseData?.self_appraisal_list?.[0]?.okrs || [];
      console.log('🔍 Debug - Direct OKRs extraction:', okrs);
      console.log('🔍 Debug - OKRs length:', okrs.length);
      console.log('🔍 Debug - Using path: response.data.self_appraisal_list[0].okrs');
      
      if (Array.isArray(okrs) && okrs.length > 0) {
        console.log('🔍 Debug - Raw okrs array:', okrs);
        transformedOKRs = okrs.map((okr: any, index: number) => ({
          sno: index + 1,
          objective: okr.objective || '',
          target: okr.target || '',
          endYearResults: okr.eyr || '',
          rating: okr.rating || 0
        }));
        console.log('🔍 Debug - transformedOKRs:', transformedOKRs);
        console.log('🔍 Debug - transformedOKRs.length:', transformedOKRs.length);
      } else {
        console.warn('⚠️ No OKRs found in API response');
        console.log('🔍 Debug - Full API response structure:', JSON.stringify(response.data, null, 2));
        console.log('🔍 Debug - self_appraisal_list:', apiResponseData?.self_appraisal_list);
        if (apiResponseData?.self_appraisal_list?.[0]) {
          console.log('🔍 Debug - First appraisal item:', apiResponseData.self_appraisal_list[0]);
        }
      }
      
      const transformedData: TeamReviewOKRsData = {
        okrs: transformedOKRs
      };
      
      console.log('🔍 Debug - Final transformed data:', JSON.stringify(transformedData, null, 2));
      
      // Check if the API response indicates success
      const apiSuccess = (response.data as any)?.status === true;
      console.log('🔍 Debug - API success status:', apiSuccess);
      console.log('🔍 Debug - API response status value:', (response.data as any)?.status);
      
      return {
        status: true, // Always return true for now to see the data structure
        data: transformedData,
        message: apiSuccess ? 'Self appraisal OKRs fetched successfully' : 'No OKRs data available'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error fetching self appraisal OKRs:', error);
      
      // Return dummy data for now
      const dummyData: TeamReviewOKRsData = {
        okrs: [
          {
            sno: 1,
            objective: "Improve customer satisfaction scores by 15%",
            target: "90% satisfaction",
            endYearResults: "92% achieved",
            rating: 5
          },
          {
            sno: 2,
            objective: "Lead digital transformation initiative",
            target: "Complete 3 projects",
            endYearResults: "4 projects completed",
            rating: 5
          },
          {
            sno: 3,
            objective: "Develop team capabilities through training",
            target: "Train 10 team members",
            endYearResults: "12 members trained",
            rating: 4
          },
          {
            sno: 4,
            objective: "Reduce operational costs by 10%",
            target: "$100K savings",
            endYearResults: "$85K saved",
            rating: 3
          },
          {
            sno: 5,
            objective: "Implement new quality processes",
            target: "2 new processes",
            endYearResults: "1 process implemented",
            rating: 2
          }
        ]
      };
      
      return {
        status: false,
        data: dummyData,
        error: error.response?.data?.message || error.message || 'Failed to fetch self appraisal OKRs, using dummy data'
      };
    }
  }

  /**
   * Get manager view OKRs by manager email and employee ID (for manager view)
   * GET /api/v1/feedback/manager/self_appraisal/okr/?manager_email=email&emp_id=empId
   * Uses same data transformation as admin API but with manager endpoint
   */
  async getManagerViewOKRs(managerEmail: string, empId: string): Promise<TeamReviewOKRsResponse> {
    try {
      console.log('===== SERVICE: STARTING MANAGER OKRS API CALL =====');
      console.log('SERVICE Step 1 - Input parameters:');
      console.log('  - Manager Email:', managerEmail);
      console.log('  - Employee ID:', empId);
      
      const apiUrl = `/api/v1/feedback/manager/self_appraisal/okr/?manager_email=${encodeURIComponent(managerEmail)}&emp_id=${encodeURIComponent(empId)}`;
      console.log('  - Full API URL:', apiUrl);
      
      console.log('SERVICE Step 2 - Making API call...');
      const response = await apiService.get(apiUrl);

      console.log('SERVICE Step 3 - Got API response:');
      console.log('  - Response exists:', !!response);
      console.log('  - Response.data exists:', !!response.data);
      console.log('SERVICE - Full response object:', response);
      console.log('SERVICE - Raw response.data:', JSON.stringify(response.data, null, 2));
      
      // Use the same transformation logic as admin API since data structure is the same
      let transformedOKRs: TeamReviewOKR[] = [];
      
      console.log('🔍 Debug - response.data:', response.data);
      console.log('🔍 Debug - response.data.status:', (response.data as any)?.status);
      console.log('🔍 Debug - self_appraisal_list:', (response.data as any)?.self_appraisal_list);
      console.log('🔍 Debug - self_appraisal_list[0]:', (response.data as any)?.self_appraisal_list?.[0]);
      console.log('🔍 Debug - okrs array:', (response.data as any)?.self_appraisal_list?.[0]?.okrs);
      
      // Extract OKRs using the same logic as admin API
      const apiResponseData = (response.data as any);
      const okrs = apiResponseData?.self_appraisal_list?.[0]?.okrs || [];
      
      // Extract manager rating/comments from the API response
      const managerRating = apiResponseData?.manager_rating || apiResponseData?.self_appraisal_list?.[0]?.manager_rating || 0;
      const rawManagerComments =
        apiResponseData?.manager_comments ||
        apiResponseData?.manager_comment ||
        apiResponseData?.manager_feedback ||
        apiResponseData?.comments ||
        apiResponseData?.self_appraisal_list?.[0]?.manager_comments ||
        apiResponseData?.self_appraisal_list?.[0]?.manager_comment ||
        apiResponseData?.self_appraisal_list?.[0]?.manager_feedback ||
        apiResponseData?.self_appraisal_list?.[0]?.comments ||
        '';
      const managerComments =
        typeof rawManagerComments === 'string' ? rawManagerComments : '';
      console.log('🔍 Debug - Extracted manager_rating:', managerRating);
      
      console.log('🔍 Debug - Direct OKRs extraction:', okrs);
      console.log('🔍 Debug - OKRs length:', okrs.length);
      
      if (Array.isArray(okrs) && okrs.length > 0) {
        console.log('🔍 Debug - Raw okrs array:', okrs);
        transformedOKRs = okrs.map((okr: any, index: number) => ({
          sno: index + 1,
          objective: okr.objective || '',
          target: okr.target || '',
          endYearResults: okr.eyr || '',
          rating: okr.rating || 0
        }));
        console.log('🔍 Debug - transformedOKRs:', transformedOKRs);
      } else {
        console.warn('⚠️ No OKRs found in manager API response');
        console.log('🔍 Debug - Full API response structure:', JSON.stringify(response.data, null, 2));
      }
      
      const transformedData: TeamReviewOKRsData = {
        okrs: transformedOKRs,
        manager_rating: managerRating,
        manager_comments: managerComments
      };
      
      console.log('🔍 Debug - Final transformed data:', JSON.stringify(transformedData, null, 2));
      
      const apiSuccess = (response.data as any)?.status === true;
      console.log('🔍 Debug - API success status:', apiSuccess);
      
      return {
        status: true,
        data: transformedData,
        message: apiSuccess ? 'Manager OKRs fetched successfully' : 'No OKRs data available'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error fetching manager OKRs:', error);
      
      return {
        status: false,
        data: { okrs: [] },
        error: error.response?.data?.message || error.message || 'Failed to fetch manager OKRs'
      };
    }
  }

  /**
   * Get manager self appraisal OKRs by manager email and employee ID (for manager view)
   * GET /api/v1/feedback/manager/self_appraisal/okr/?manager_email=email&emp_id=empId
   * Returns the same data structure as admin API
   */
  async getManagerSelfAppraisalOKRs(managerEmail: string, empId: string): Promise<TeamReviewOKRsResponse> {
    try {
      console.log('===== SERVICE: STARTING MANAGER OKRS API CALL =====');
      console.log('SERVICE Step 1 - Input parameters:');
      console.log('  - Manager Email:', managerEmail);
      console.log('  - Employee ID:', empId);
      
      const apiUrl = `/api/v1/feedback/manager/self_appraisal/okr/?manager_email=${encodeURIComponent(managerEmail)}&emp_id=${encodeURIComponent(empId)}`;
      console.log('  - Full API URL:', apiUrl);
      
      console.log('SERVICE Step 2 - Making API call...');
      const response = await apiService.get(apiUrl);

      console.log('SERVICE Step 3 - Got API response:');
      console.log('  - Response exists:', !!response);
      console.log('  - Response.data exists:', !!response.data);
      console.log('SERVICE - Full response object:', response);
      console.log('SERVICE - Raw response.data:', JSON.stringify(response.data, null, 2));
      
      // Use the same transformation logic as admin API since data structure is the same
      let transformedOKRs: TeamReviewOKR[] = [];
      
      console.log('🔍 Debug - response.data:', response.data);
      console.log('🔍 Debug - response.data.status:', (response.data as any)?.status);
      console.log('🔍 Debug - self_appraisal_list:', (response.data as any)?.self_appraisal_list);
      
      const apiResponseData = response.data as any;
      if (apiResponseData && apiResponseData.self_appraisal_list && Array.isArray(apiResponseData.self_appraisal_list)) {
        console.log('🔍 Debug - Found self_appraisal_list, length:', apiResponseData.self_appraisal_list.length);
        
        transformedOKRs = apiResponseData.self_appraisal_list.map((okr: any, index: number) => ({
          sno: index + 1,
          objective: okr.objective || `Objective ${index + 1}`,
          target: okr.target || 'N/A',
          endYearResults: okr.end_year_results || okr.endYearResults || 'N/A',
          rating: okr.rating || 0
        }));
        
        if (transformedOKRs.length > 0) {
          console.log('🔍 Debug - First transformed OKR:', transformedOKRs[0]);
        }
      }
      
      // Extract manager_rating from the API response
      const managerRating = apiResponseData?.manager_rating || apiResponseData?.self_appraisal_list?.[0]?.manager_rating || 0;
      console.log('🔍 Debug - Extracted manager_rating:', managerRating);
      
      const transformedData: TeamReviewOKRsData = {
        okrs: transformedOKRs,
        manager_rating: managerRating
      };
      
      console.log('🔍 Debug - Final transformed data:', JSON.stringify(transformedData, null, 2));
      
      const apiSuccess = (response.data as any)?.status === true;
      console.log('🔍 Debug - API success status:', apiSuccess);
      
      return {
        status: true,
        data: transformedData,
        message: apiSuccess ? 'Manager OKRs fetched successfully' : 'No OKRs data available'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error fetching manager OKRs:', error);
      
      return {
        status: false,
        data: { okrs: [] },
        error: error.response?.data?.message || error.message || 'Failed to fetch manager OKRs'
      };
    }
  }

  /**
   * Get nomination review details for a specific employee
   */
  async getNominationReviewDetails(email: string, nominationRelationshipId: string): Promise<any> {
    try {
      console.log('🔄 TeamReviewService - Fetching nomination review details for:', { email, nominationRelationshipId });
      
      const response = await apiService.get(`/api/v1/feedback/nomination_review/?email=${email}&nomination_relationship_id=${nominationRelationshipId}`);
      
      console.log('📋 TeamReviewService - Nomination review details response:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Nomination review details fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error fetching nomination review details:', error);
      
      return {
        status: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch nomination review details'
      };
    }
  }

  /**
   * Get nomination review feedbacks for View Feedback tab
   */
  async getNominationReviewFeedbacks(email: string, nominationRelationshipId: string): Promise<any> {
    try {
      console.log('🔄 TeamReviewService - Fetching nomination review feedbacks for:', { email, nominationRelationshipId });
      
      const response = await apiService.get(`/api/v1/feedback/nomination_review_feedbacks/?email=${email}&nomination_relationship_id=${nominationRelationshipId}`);
      
      console.log('📋 TeamReviewService - Nomination review feedbacks response:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Nomination review feedbacks fetched successfully'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error fetching nomination review feedbacks:', error);
      
      return {
        status: false,
        data: null,
        error: error.response?.data?.message || error.message || 'Failed to fetch nomination review feedbacks'
      };
    }
  }

  /**
   * Save nomination review feedback (Save as Draft or Submit)
   */
  async saveNominationReviewFeedback(data: {
    nomination_relationship_id: number;
    email: string;
    competency: {
      solution_focused: number;
      adapt_to_win: number;
      courage_to_act: number;
      act_for_enterprise: number;
      own_it: number;
    };
    feedback: {
      constructive: string;
      appreciative: string;
    };
    is_email_required?: string;
  }): Promise<any> {
    try {
      console.log('🔄 TeamReviewService - Saving nomination review feedback:', data);
      
      const response = await apiService.put('/api/v1/feedback/nomination_review/', data);
      
      console.log('✅ TeamReviewService - Nomination review feedback saved successfully:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Nomination review feedback saved successfully'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error saving nomination review feedback:', error);
      
      // Handle API error response properly
      const apiError = error.response?.data;
      
      return {
        status: false,
        data: apiError?.data || null,
        message: apiError?.message || error.message || 'Failed to save nomination review feedback',
        error: apiError?.error || error.message || 'Failed to save nomination review feedback',
        status_code: apiError?.status_code || error.response?.status
      };
    }
  }

  /**
   * Submit nomination review feedback (Final submission)
   */
  async submitNominationReviewFeedback(data: {
    nomination_relationship_id: number;
    email: string;
    competency: {
      solution_focused: number;
      adapt_to_win: number;
      courage_to_act: number;
      act_for_enterprise: number;
      own_it: number;
    };
    feedback: {
      constructive: string;
      appreciative: string;
    };
    is_email_required?: string;
  }): Promise<any> {
    try {
      console.log('🔄 TeamReviewService - Submitting nomination review feedback:', data);
      
      const response = await apiService.post('/api/v1/feedback/nomination_review/', data);
      
      console.log('✅ TeamReviewService - Nomination review feedback submitted successfully:', response);
      
      return {
        status: true,
        data: response.data,
        message: 'Nomination review feedback submitted successfully'
      };
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error submitting nomination review feedback:', error);
      
      // Handle API error response properly
      const apiError = error.response?.data;
      
      return {
        status: false,
        data: apiError?.data || null,
        message: apiError?.message || error.message || 'Failed to submit nomination review feedback',
        error: apiError?.error || error.message || 'Failed to submit nomination review feedback',
        status_code: apiError?.status_code || error.response?.status
      };
    }
  }

  /**
   * Update manager OKR rating for an employee (manager view)
   * PUT /api/v1/feedback/manager/self_appraisal/okr/
   */
  async updateManagerOkrRating(
    empId: string,
    managerEmail: string,
    selectedRating: number,
    managerComments: string
  ): Promise<any> {
    try {
      console.log('🔄 TeamReviewService - Updating manager OKR rating');
      console.log('  - Employee ID:', empId);
      console.log('  - Selected Rating (will be sent as manager_rating):', selectedRating);
      console.log('  - Manager Email:', managerEmail);
      console.log('  - Manager Comments Length:', managerComments.trim().length);

      const payload = {
        emp_id: empId,
        manager_rating: selectedRating, // The selected rating goes to manager_rating
        manager_email: managerEmail,
        manager_comments: managerComments.trim(),
     //   overall_rating: 0 // Always send 0 for overall_rating
      };

      console.log('📤 TeamReviewService - Sending payload:', {
        ...payload,
        manager_comments: payload.manager_comments ? '[provided]' : ''
      });

      const response = await apiService.put('/api/v1/feedback/manager/self_appraisal/okr/', payload);

      console.log('✅ TeamReviewService - Manager OKR rating updated:', response);
      return response;
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error updating manager OKR rating:', error);
      throw error;
    }
  }

  /**
   * Update manager OKR rating for an employee (manager view)
   * PUT /api/v1/manager/admin/self_appraisal/okr/
   */
  async updateManagerViewOkrRating(
    empId: string,
    managerEmail: string,
    overallRating: number
  ): Promise<any> {
    try {
      console.log('🔄 TeamReviewService - Updating manager view OKR rating');
      console.log('  - Employee ID:', empId);
      console.log('  - Manager Email:', managerEmail);
      console.log('  - Overall Rating:', overallRating);

      const payload = {
        emp_id: empId,
        manager_email: managerEmail,
      //  overall_rating: overallRating
      };

      const response = await apiService.put('/api/v1/manager/admin/self_appraisal/okr/', payload);

      console.log('✅ TeamReviewService - Manager view OKR rating updated:', response);
      return response;
    } catch (error: any) {
      console.error('❌ TeamReviewService - Error updating manager view OKR rating:', error);
      throw error;
    }
  }

  // Character validation utility methods
  getCharacterCount(text: string): number {
    return text ? text.length : 0;
  }

  isValidCharacterCount(text: string): { isValid: boolean; count: number; message?: string } {
    const count = this.getCharacterCount(text);
    const MIN_CHARS = 10;
    const MAX_CHARS = 10000;

    if (count < MIN_CHARS) {
      return {
        isValid: false,
        count,
        message: `Minimum ${MIN_CHARS} characters required`
      };
    }

    if (count > MAX_CHARS) {
      return {
        isValid: false,
        count,
        message: `Maximum ${MAX_CHARS} characters allowed`
      };
    }

    return {
      isValid: true,
      count
    };
  }
}

export const teamReviewService = new TeamReviewService();
